import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Dropdown } from 'antd'

import { useAuthStore } from 'store/useAuthStore'
import { logout } from 'api/auth.api'

import { LogoutIcon, PasswordChangeIcon } from 'assets/icons'
import 'assets/scss/layout/Header.scss'

const userName = '사용자'
const environment = process.env.REACT_APP_ENVIRONMENT
const isDev = environment === 'dev' || environment === 'local'

const Header = () => {
    // store
    const { isLogin, userInfo, reset } = useAuthStore()

    // hooks
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const path = pathname.split('/')[1]

    // state
    const [activeMenuItem, setActiveMenuItem] = useState(null) // 1depth 메뉴

    const toolMenu = [
        {
            label: '자산 관리',
            key: '/tools/inventory',
        },
        {
            type: 'divider',
        },
        {
            label: '대여 반납',
            key: '/tools/rental',
        },
    ]
    const statisticsMenu = [
        { label: '지역별 데이터', key: '/statistics' },
        { type: 'divider' },
        { label: '통합 데이터', key: '/statistics/total' },
    ]
    const csMenu = [
        { label: '공지사항', key: '/notice' },
        { type: 'divider' },
        { label: 'FAQ', key: '/contact/faq' },
        { type: 'divider' },
        { label: '문의하기', key: '/contact' },
    ]

    // 강사 메뉴
    const tutorMenuItems = [{ id: 1, text: '마이페이지', to: '/tutor-mypage' }]

    const commonMenuItems = [
        { id: 1, text: '교육실적', to: '/program' },
        { id: 2, text: '강사정보', to: '/tutors' },
        { id: 3, text: '성과 분석', to: '/statistics' },
    ]
    // 용역사 계정 메뉴
    const serviceManagerMenuItems = [...commonMenuItems, { id: 4, text: '문의하기', to: '/contact' }]

    // 지역 관리자 메뉴
    const menuItems = [
        ...commonMenuItems,
        { id: 4, text: '자산', to: '/tools/inventory' },
        { id: 5, text: '월간보고', to: '/report' },
        { id: 6, text: '업무지원', to: '/notice' },
    ]
    // 어드민 메뉴
    const adminMenuItems = [
        ...menuItems,
        { id: 7, text: '계정관리', to: '/accounts' },
        ...(isDev ? [{ id: 8, text: 'AI 프롬프트', to: '/dev/prompt-studio' }] : []),
    ]

    // userInfo?.isServiceManager : 용역사 계정
    // userInfo?.isTutor : 강사 계정
    const [menus, setMenus] = useState(
        userInfo?.isMaster
            ? adminMenuItems
            : userInfo?.isTutor
            ? tutorMenuItems
            : userInfo?.isServiceManager
            ? serviceManagerMenuItems
            : menuItems,
    )

    const handleDropdownItemClick = e => {
        navigate(e.key)
    }
    const handleMenuItemClick = index => {
        setActiveMenuItem(index)
    }

    const handleLogout = async () => {
        try {
            await logout()
        } finally {
            reset()
            navigate(userInfo?.isTutor ? '/tutor-login' : '/login')
        }
    }

    /**
     * URL patname에 따라 메뉴 active 상태 변경
     * @param pathName
     * @param Array
     * @returns {*|null}
     */
    const findMenuItemIdByPathname = (pathName, Array) => {
        const matchingItem = Array.find(item => {
            const currentPathSegment = pathname.split('/')[1]
            const menuPathSegment = item.to.split('/')[1]

            if (item.text === '업무지원') {
                return currentPathSegment === 'contact' || currentPathSegment === 'notice'
            }

            return menuPathSegment === currentPathSegment
        })
        return matchingItem ? matchingItem.id : null
    }

    useEffect(() => {
        let selectedMenuItems
        if (userInfo?.isMaster) {
            selectedMenuItems = adminMenuItems
        } else if (userInfo?.isTutor) {
            selectedMenuItems = tutorMenuItems
        } else {
            selectedMenuItems = userInfo?.isServiceManager ? serviceManagerMenuItems : menuItems
        }
        setMenus(selectedMenuItems)
        const menuItemId = findMenuItemIdByPathname(path, selectedMenuItems)
        setActiveMenuItem(menuItemId - 1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, userInfo?.isMaster, userInfo?.isServiceManager, userInfo?.isTutor])

    return (
        <div id="header">
            <div className="container">
                <button type="button" className="button-reset-type">
                    <Link to={userInfo?.isTutor ? '/tutor-mypage' : '/'}>
                        <div className="logo_wrap">
                            <div className="logo" />
                            <span className="h8">성과 관리 시스템 {isDev && '(개발용)'} </span>
                        </div>
                    </Link>
                </button>
                {isLogin && (
                    <div className="menu_wrap">
                        <div className="menu">
                            {menus
                                .filter(item => !item.invisible) // invisible이 true면 필터링
                                .map((item, index) => {
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`${
                                                activeMenuItem === index
                                                    ? 'active button-reset-type menu-btn'
                                                    : 'button-reset-type menu-btn'
                                            }`}
                                            onClick={() => handleMenuItemClick(index)}
                                        >
                                            {item.text === '자산' ? (
                                                <Dropdown
                                                    menu={{
                                                        onClick: handleDropdownItemClick,
                                                        items: toolMenu,
                                                    }}
                                                    placement="bottom"
                                                    overlayClassName="tools_overlay_menu"
                                                >
                                                    <Link className="header_menu h92" to={item.to}>
                                                        <span className="slim_text4 tool">{item.text}</span>
                                                    </Link>
                                                </Dropdown>
                                            ) : item.text === '성과 분석' ? (
                                                userInfo?.isMaster ? (
                                                    <Dropdown
                                                        menu={{
                                                            onClick: handleDropdownItemClick,
                                                            items: statisticsMenu,
                                                        }}
                                                        placement="bottom"
                                                        overlayClassName="tools_overlay_menu"
                                                    >
                                                        <Link className="header_menu h92" to={item.to}>
                                                            <span className="slim_text4 tool">{item.text}</span>
                                                        </Link>
                                                    </Dropdown>
                                                ) : (
                                                    <Link className="header_menu h92" to={item.to}>
                                                        <span className="slim_text4 tool">{item.text}</span>
                                                    </Link>
                                                )
                                            ) : item.text === '업무지원' ? (
                                                <Dropdown
                                                    menu={{
                                                        onClick: handleDropdownItemClick,
                                                        items: csMenu,
                                                    }}
                                                    placement="bottom"
                                                    overlayClassName="tools_overlay_menu"
                                                >
                                                    <Link className="header_menu h92" to={item.to}>
                                                        <span className="slim_text4 tool">{item.text}</span>
                                                    </Link>
                                                </Dropdown>
                                            ) : (
                                                <Link className="header_menu" to={item.to}>
                                                    <span className="slim_text4">{item.text}</span>
                                                </Link>
                                            )}
                                        </button>
                                    )
                                })}
                        </div>
                        <div className="login">
                            <div className="info">
                                <div className="sub_title2">
                                    {`[${userInfo?.isMaster ? 'Admin' : userInfo?.regionId}]`}{' '}
                                    <p className="body2 gray">{userInfo?.isTutor ? userInfo.tutorName : userName}님</p>
                                </div>
                            </div>
                            <Link to="/accounts/password" className="password-change">
                                <PasswordChangeIcon />
                                <span>비밀번호 변경</span>
                            </Link>
                            <button type="button" className="button-reset-type" onClick={handleLogout}>
                                <LogoutIcon />
                                <span>로그아웃</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default Header
